import React, { useState } from "react";
import { CollapsibleFormSection } from "../../common/CollapsibleFormSection";
import "../../../../styles/components/forms/strategy-selector.css";
import { useFunnyMessages } from "src/hooks/useFunnyMessages";

interface OtherStrategiesSectionProps {
  strategies: any[];
  isLoading: boolean;
  error: string | null;
  onGenerateStrategies: () => void;
  onStrategyToggle: (strategy: string) => void;
  selectedStrategies: string[];
  smartGoal: string | null;
  goalType: string;
  className?: string;
}

export const OtherStrategiesSection: React.FC<OtherStrategiesSectionProps> = ({
  strategies,
  isLoading,
  error,
  onGenerateStrategies,
  onStrategyToggle,
  selectedStrategies,
  smartGoal,
  goalType,
  className,
}) => {
  const [showStrategies, setShowStrategies] = useState(strategies.length > 0);
  const funnyMessage = useFunnyMessages(isLoading);

  const handleGenerateClick = () => {
    setShowStrategies(true);
    onGenerateStrategies();
  };

  return (
    <CollapsibleFormSection
      title={`${goalType || "Other"} Strategies`}
      className={className}
    >
      {!smartGoal ? (
        <p className="form-hint">
          Please select a goal first to generate strategies.
        </p>
      ) : !showStrategies ? (
        <div className="form-group center-button-container">
          <button
            type="button"
            className="primary-button"
            onClick={handleGenerateClick}
            disabled={isLoading || !smartGoal}
          >
            Generate strategies
          </button>
        </div>
      ) : (
        <div className="strategies-container">
          {isLoading ? (
            <div className="loading-indicator">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray="32"
                  strokeDashoffset="10"
                />
              </svg>
              <span>{funnyMessage}</span>
            </div>
          ) : (
            <>
              <h3 className="strategies-subtitle">
                Which strategies would you be happy to try?
              </h3>
              <p className="form-note strategies-note">
                (You can always tweak things after download)
              </p>

              <div className="form-group">
                {error && <p className="form-error">{error}</p>}

                {strategies.map((strategy, index) => (
                  <div
                    key={index}
                    className={`strategy-item ${
                      selectedStrategies.includes(strategy.text)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => onStrategyToggle(strategy.text)}
                  >
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id={`other-strategy-${index}`}
                        name={`otherStrategy-${index}`}
                        checked={selectedStrategies.includes(strategy.text)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onStrategyToggle(strategy.text);
                        }}
                      />
                      <div className="checkbox-custom"></div>
                      <label
                        className="checkbox-label"
                        htmlFor={`other-strategy-${index}`}
                      >
                        {strategy.text}
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              {selectedStrategies.length < 1 && (
                <p className="form-hint">Please select at least 1 strategy.</p>
              )}

              <div className="form-group">
                <div className="tooltip-container">
                  <button
                    type="button"
                    className="secondary-button regenerate-button"
                    onClick={onGenerateStrategies}
                    disabled={isLoading}
                  >
                    Regenerate Strategies
                  </button>

                  {selectedStrategies.length < 1 && (
                    <div className="tooltip">
                      <p>Please select at least 1 strategy</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </CollapsibleFormSection>
  );
};
